import { toast } from 'sonner';
import { parseClipboardData } from './dataFormatters';
import { computeAutoColor } from './gridEventHandlers';

/**
 * Handle paste click - read clipboard and show preview
 */
export const handlePasteClickHelper = async (tableType, setPastePreviewData, setPasteTargetTable, setShowPasteDialog) => {
  try {
    const clipboardText = await navigator.clipboard.readText();
    const parsedData = parseClipboardData(clipboardText);
    
    if (parsedData.length === 0) {
      toast.error('Ingen data funnet i clipboard');
      return;
    }
    
    setPastePreviewData(parsedData);
    setPasteTargetTable(tableType);
    setShowPasteDialog(true);
  } catch (error) {
    toast.error('Kunne ikke lese clipboard. Prøv å kopiere data på nytt.');
    console.error('Clipboard error:', error);
  }
};

/**
 * Apply pasted data to the appropriate table
 */
export const applyPastedDataHelper = ({
  pasteTargetTable,
  pastePreviewData,
  organisms,
  currentLocations,
  locations,
  gridData,
  zooplanktonGridData,
  currentZooplanktonOrganisms,
  calculateSums,
  setGridData,
  setZooplanktonGridData,
  setShowPasteDialog,
  setColorOverrides
}) => {
  if (pasteTargetTable === 'algae') {
    applyToAlgaeTableHelper({
      pastePreviewData,
      organisms,
      currentLocations,
      locations,
      gridData,
      calculateSums,
      setGridData,
      setColorOverrides
    });
  } else if (pasteTargetTable === 'zooplankton') {
    applyToZooplanktonTableHelper({
      pastePreviewData,
      currentZooplanktonOrganisms,
      currentLocations,
      locations,
      zooplanktonGridData,
      setZooplanktonGridData
    });
  }
  
  setShowPasteDialog(false);
  toast.success('Data importert!');
};

/**
 * Apply pasted data to algae table
 */
const applyToAlgaeTableHelper = ({
  pastePreviewData,
  organisms,
  currentLocations,
  locations,
  gridData,
  calculateSums,
  setGridData,
  setColorOverrides
}) => {
  
  const newGridData = { ...gridData };
  const newColors = {};
  const locationsList = currentLocations.length > 0 ? currentLocations : locations;
  let clipboardRowIndex = 0;
  
  organisms.forEach((org, tableRowIndex) => {
    if (org.isSum) {
      clipboardRowIndex++;
      return;
    }
    
    if (clipboardRowIndex < pastePreviewData.length) {
      const organismKey = `${org.group}_${org.species}`;
      const row = pastePreviewData[clipboardRowIndex];
      
      // Ensure the organism key exists in gridData
      if (!newGridData[organismKey]) {
        newGridData[organismKey] = {};
      }
      
      row.forEach((cellValue, colIndex) => {
        if (colIndex < locationsList.length) {
          const location = locationsList[colIndex];
          newGridData[organismKey][location] = cellValue;
          // Auto-compute color
          const color = computeAutoColor(cellValue, org.group, org.species, organisms);
          const overrideKey = `${organismKey}_${location}`;
          if (color) {
            newColors[overrideKey] = color;
          }
        }
      });
      
      clipboardRowIndex++;
    }
  });
  
  const dataWithSums = calculateSums(newGridData);
  setGridData(dataWithSums);
  if (setColorOverrides && Object.keys(newColors).length > 0) {
    setColorOverrides(prev => ({ ...prev, ...newColors }));
  }
};

/**
 * Apply pasted data to zooplankton table
 */
const applyToZooplanktonTableHelper = ({
  pastePreviewData,
  currentZooplanktonOrganisms,
  currentLocations,
  locations,
  zooplanktonGridData,
  setZooplanktonGridData
}) => {
  
  const newGridData = { ...zooplanktonGridData };
  const editableOrganismKeys = currentZooplanktonOrganisms
    .filter(org => !org.organism?.includes('SUM'))
    .map(org => `${org.sampleType}_${org.organism}`);
  
  const locationsList = currentLocations.length > 0 ? currentLocations : locations;
  
  // Map clipboard data to editable rows
  pastePreviewData.forEach((row, sourceRowIndex) => {
    if (sourceRowIndex < editableOrganismKeys.length) {
      const organismKey = editableOrganismKeys[sourceRowIndex];
      
      // Ensure the organism key exists in gridData
      if (!newGridData[organismKey]) {
        newGridData[organismKey] = {};
      }
      
      row.forEach((cellValue, colIndex) => {
        if (colIndex < locationsList.length) {
          const location = locationsList[colIndex];
          
          // Normalize zooplankton values
          let normalizedValue = cellValue;
          if (cellValue === '<5' || cellValue === '< 5' || cellValue === '<5000' || cellValue === '< 5000') {
            normalizedValue = '<5';
          } else if (['+', '++', '+++', '-'].includes(cellValue)) {
            normalizedValue = cellValue;
          }
          
          newGridData[organismKey][location] = normalizedValue;
        }
      });
    }
  });
  
  setZooplanktonGridData(newGridData);
};
